import { useEffect, useState } from 'react';
import { getFirestore, collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { ActivityCalendar } from 'react-activity-calendar';
import { useTheme } from '../contexts/ThemeContext';

interface ContributionGraphProps {
  userId: string;
  onDateSelect?: (date: string) => void;
}

interface ActivityData {
  date: string;
  count: number;
  level: 0 | 1 | 2 | 3 | 4;
}

const DEFAULT_DAILY_GOAL = 5;

export default function ContributionGraph({ userId, onDateSelect }: ContributionGraphProps) {
  const [data, setData] = useState<ActivityData[]>([]);
  const [loading, setLoading] = useState(true);
  const [dailyGoal, setDailyGoal] = useState(DEFAULT_DAILY_GOAL);
  const { resolvedTheme } = useTheme();

  useEffect(() => {
    loadUserSettings();
  }, [userId]);

  useEffect(() => {
    loadStatistics();
  }, [userId, dailyGoal]);

  const loadUserSettings = async () => {
    try {
      const db = getFirestore();
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const settings = userDoc.data()?.settings;
        if (settings?.dailyApplicationGoal) {
          setDailyGoal(settings.dailyApplicationGoal);
        }
      }
    } catch (error) {
      console.error('Error loading user settings:', error);
    }
  };

  const calculateLevel = (count: number): 0 | 1 | 2 | 3 | 4 => {
    if (count === 0) return 0;
    if (count >= dailyGoal) return 4;  // At or above goal = darkest green
    // For small goals (1-2), use simplified levels
    if (dailyGoal <= 2) {
      return 2;
    }
    // Divide (goal - 1) into 3 intervals for levels 1-3
    const interval = Math.ceil((dailyGoal - 1) / 3);
    if (count <= interval) return 1;
    if (count <= interval * 2) return 2;
    return 3;
  };

  const loadStatistics = async () => {
    setLoading(true);
    try {
      const db = getFirestore();
      // Query applications directly instead of statistics
      const appsQuery = query(
        collection(db, 'applications'),
        where('userId', '==', userId)
      );
      const snapshot = await getDocs(appsQuery);

      const dailyMap = new Map<string, number>();

      // Count applications per day
      snapshot.forEach(doc => {
        const app = doc.data();
        if (app.appliedDate) {
          const date = app.appliedDate.toDate ? app.appliedDate.toDate() : new Date(app.appliedDate);
          // Use local timezone for consistency
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const dateStr = `${year}-${month}-${day}`;
          dailyMap.set(dateStr, (dailyMap.get(dateStr) || 0) + 1);
        }
      });

      const activityData: ActivityData[] = [];
      const today = new Date();
      const yearAgo = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());

      for (let d = new Date(yearAgo); d <= today; d.setDate(d.getDate() + 1)) {
        // Use local timezone for consistency
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;
        const count = dailyMap.get(dateStr) || 0;
        const level = calculateLevel(count);

        activityData.push({ date: dateStr, count, level });
      }

      if (activityData.length === 0) {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const todayStr = `${year}-${month}-${day}`;
        activityData.push({ date: todayStr, count: 0, level: 0 });
      }

      setData(activityData);
    } catch (error) {
      console.error('Error loading statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalApplications = data.reduce((sum, d) => sum + d.count, 0);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
      <style>{`
        /* Clickable blocks */
        .react-activity-calendar rect[data-level]:hover {
          opacity: 0.8;
          cursor: pointer;
        }

        /* Month labels - light mode */
        .react-activity-calendar text {
          font-weight: 500;
          fill: #6b7280;
        }

        /* Month labels - dark mode */
        .dark .react-activity-calendar text {
          fill: #9ca3af;
        }

        /* Hide the library's year label */
        .react-activity-calendar footer {
          display: none !important;
        }

        /* Responsive calendar container */
        .activity-calendar-container {
          width: 100%;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }

        .activity-calendar-container > div {
          min-width: fit-content;
        }

        @media (max-width: 640px) {
          .react-activity-calendar {
            font-size: 10px !important;
          }
        }
      `}</style>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4 sm:mb-6">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">Application Activity</h3>
        <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
          <span className="font-semibold">{totalApplications}</span> applications in the last year
        </div>
      </div>

      {!loading && (
        <div
          className="activity-calendar-container"
          style={{
            padding: '8px',
            borderRadius: '8px',
            backgroundColor: resolvedTheme === 'dark' ? '#1f2937' : '#fafafa',
            border: resolvedTheme === 'dark' ? '1px solid #374151' : '1px solid #e5e7eb'
          }}
        >
          <ActivityCalendar
            data={data}
            blockSize={10}
            blockMargin={3}
            blockRadius={2}
            fontSize={12}
            showWeekdayLabels
            colorScheme={resolvedTheme}
            theme={{
              light: ['#ebedf0', '#9be9a8', '#40c463', '#30a14e', '#216e39'],
              dark: ['#161b22', '#0e4429', '#006d32', '#26a641', '#39d353'],
            }}
            renderBlock={(block, activity) => {
              // Parse date in local timezone to avoid day shift
              const [year, month, day] = activity.date.split('-').map(Number);
              const date = new Date(year, month - 1, day);
              const tooltipText = `${activity.count} application${activity.count !== 1 ? 's' : ''} on ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

              return (
                <rect
                  {...block.props}
                  onClick={() => {
                    if (activity.count > 0 && onDateSelect) {
                      onDateSelect(activity.date);
                    }
                  }}
                  style={{ cursor: activity.count > 0 ? 'pointer' : 'default' }}
                >
                  <title>{tooltipText}</title>
                </rect>
              );
            }}
          />
        </div>
      )}
    </div>
  );
}
